import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { featuredVideos, latestUpdates, articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);

  const allVideos = [...featuredVideos, ...latestUpdates];
  const video = allVideos.find((v) => v.id === articleId);
  const article = articles.find((a) => a.id === articleId);

  const relatedVideos = allVideos
    .filter((v) => v.categorySlug === (video?.categorySlug || article?.categorySlug) && v.id !== articleId)
    .slice(0, 4);

  if (!video && !article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-800">内容未找到</h2>
            <p className="text-sm text-foreground-500 mt-2 mb-4">该内容可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySlug = video?.categorySlug || article?.categorySlug || '';
  const categoryName = video?.category || article?.category || '';
  const title = video?.title || article?.title || '';
  const date = video?.date || article?.date || '';
  const thumbnail = video?.thumbnail || article?.thumbnail || '';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${categorySlug}` },
                { label: title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <header className="mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-snug">
                {title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <Link
                  to={`/category/${categorySlug}`}
                  className="text-xs px-2.5 py-1 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-400">
                  <i className="ri-calendar-line mr-1"></i>
                  {date}
                </span>
                {video && (
                  <>
                    <span className="text-xs text-foreground-400">
                      <i className="ri-eye-line mr-1"></i>
                      {video.views}次观看
                    </span>
                    <span className="flex items-center gap-1 text-xs text-accent-500">
                      <i className="ri-star-fill text-xs"></i>
                      {video.rating}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-600">
                      {video.quality}
                    </span>
                  </>
                )}
                {article && (
                  <span className="text-xs text-foreground-400">
                    <i className="ri-user-line mr-1"></i>
                    {article.author}
                  </span>
                )}
              </div>
            </header>

            {/* Thumbnail / Video Placeholder */}
            <div className="relative aspect-video rounded-lg overflow-hidden mb-8 bg-foreground-950">
              <img
                src={thumbnail}
                alt={title}
                className="w-full h-full object-cover"
                title={`${title} - 小蓝视频高清在线观看`}
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer group">
                <div className="w-20 h-20 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/40 transition-all group-hover:scale-110">
                  <i className="ri-play-fill text-white text-4xl ml-1"></i>
                </div>
              </div>
              <div className="absolute bottom-3 left-3">
                <span className="px-3 py-1 rounded text-sm bg-black/60 text-white">
                  {video?.quality || '1080P'}
                </span>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose-custom">
              {video && (
                <div className="mb-6">
                  <p className="text-base text-foreground-700 leading-relaxed">{video.description}</p>
                  <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">影片信息</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-400">分类：</span>
                        <span className="text-foreground-700">{video.category}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-400">画质：</span>
                        <span className="text-foreground-700">{video.quality}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-400">评分：</span>
                        <span className="text-accent-500">{video.rating}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-400">观看：</span>
                        <span className="text-foreground-700">{video.views}</span>
                      </div>
                      {video.duration && (
                        <div className="flex items-center gap-2">
                          <span className="text-foreground-400">时长：</span>
                          <span className="text-foreground-700">{video.duration}</span>
                        </div>
                      )}
                      {video.updateInfo && (
                        <div className="flex items-center gap-2">
                          <span className="text-foreground-400">更新：</span>
                          <span className="text-primary-500">{video.updateInfo}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {article && (
                <div className="article-body text-base text-foreground-700 leading-relaxed space-y-4">
                  <p className="text-foreground-600 italic border-l-3 border-primary-300 pl-4 py-1">
                    {article.excerpt}
                  </p>
                  <div dangerouslySetInnerHTML={{ __html: article.content }}></div>
                  {article.tags && (
                    <div className="flex flex-wrap items-center gap-2 pt-4 mt-6 border-t border-background-200/70">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedVideos.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/article/${rv.id}`}
                      className="group flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="relative w-32 aspect-video rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          title={`${rv.title} - 小蓝视频`}
                        />
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center bg-black/30">
                          <i className="ri-play-fill text-white text-2xl"></i>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rv.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs text-foreground-400">{rv.category}</span>
                          <span className="text-xs text-accent-500">
                            <i className="ri-star-fill text-[10px] mr-0.5"></i>
                            {rv.rating}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        {/* Schema.org Article */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": article ? "Article" : "VideoObject",
            "headline": title,
            "description": video?.description || article?.excerpt || '',
            "datePublished": date,
            ...(article ? {
              "author": {
                "@type": "Person",
                "name": article.author,
              },
            } : {}),
            "thumbnailUrl": thumbnail,
          }),
        }} />
      </main>

      <Footer />

      <style>{`
        .article-body p {
          margin-bottom: 1rem;
          line-height: 1.8;
        }
        .article-body h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .article-body h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </>
  );
}